/**
 * Fase 38 — Findings inbox.
 *
 * Lista paginada con filtros básicos (status, severity≥, category, module).
 * Cada row linkea al detail (/findings/:id) y al SessionDetail original.
 */
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchFindings,
  type FindingListItem,
  type FindingsListFilters,
} from '../../api/admin/findings.js';
import { shortId } from '../../lib/format.js';
import { GATED_CONTENT_PLACEHOLDER } from '../../lib/privacy.js';
import { useUrlParam, useUrlParamWithStorage } from '../../lib/useUrlTab.js';

type StatusFilter = 'all' | 'new' | 'triaged' | 'accepted' | 'rejected' | 'shipped' | 'duplicate';
const STATUSES: StatusFilter[] = ['all', 'new', 'triaged', 'accepted', 'rejected', 'shipped', 'duplicate'];
const SEVERITIES = [1, 2, 3, 4, 5];

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const delta = Math.max(0, Date.now() - then);
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(iso).toLocaleString();
}

function SeverityBadge({ value }: { value: number }): ReactElement {
  const cls =
    value >= 5
      ? 'badge b-danger'
      : value === 4
        ? 'badge b-warn'
        : value === 3
          ? 'badge b-info'
          : 'badge';
  return <span className={cls}>S{value}</span>;
}

function StatusBadge({ status }: { status: string }): ReactElement {
  const cls =
    status === 'new'
      ? 'badge b-info'
      : status === 'accepted'
        ? 'badge b-success'
        : status === 'rejected'
          ? 'badge'
          : status === 'shipped'
            ? 'badge b-success'
            : status === 'duplicate'
              ? 'badge'
              : 'badge';
  return <span className={cls}>{status}</span>;
}

export function FindingsListPage(): ReactElement {
  // Link "ver regressions" de ShippedFixes: `/findings?regression_of=<id>`.
  // Con el filtro activo el status inicial es `all` — las regressions pueden
  // estar en cualquier status y el default `new` las escondería.
  const [regressionOf, setRegressionOf] = useUrlParam('regression_of');
  const [status, setStatus] = useState<StatusFilter>(regressionOf ? 'all' : 'new');
  const [minSeverity, setMinSeverity] = useState<number>(0);
  const [moduleFilter, setModuleFilter] = useState<string>('');
  // Fase 39 — toggle para incluir findings derivados de sesiones sintéticas.
  // Default OFF (backend filtra from_synthetic=false sin esto). Persistido en
  // localStorage: si el user tildó antes y vuelve a /findings desde el sidebar,
  // el filtro queda tildado.
  const [synFlag, setSynFlag] = useUrlParamWithStorage(
    'synthetic',
    'admin.findings.includeSynthetic',
  );
  const includeSynthetic = synFlag === '1';
  const setIncludeSynthetic = (next: boolean): void => {
    setSynFlag(next ? '1' : null);
  };

  const filters: FindingsListFilters = {
    ...(status === 'all' ? {} : { status }),
    ...(minSeverity > 0 ? { minSeverity } : {}),
    ...(moduleFilter ? { module: moduleFilter } : {}),
    ...(includeSynthetic ? { includeSynthetic: true } : {}),
    ...(regressionOf ? { regressionOf } : {}),
    limit: 50,
  };

  const query = useQuery({
    queryKey: [
      'admin',
      'findings',
      status,
      minSeverity,
      moduleFilter,
      includeSynthetic,
      regressionOf,
    ],
    queryFn: () => fetchFindings(filters),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Findings</h1>
          <p className="page-sub">
            Offline Session Analysis (Fase 38) — refresh 30s. <code className="mono">propose_finding</code>{' '}
            del Reader, filtrado por el Critic.
          </p>
        </div>
      </div>

      <section className="sess-filters" data-testid="findings-filters">
        <span className="label">Status</span>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${status === s ? ' is-on' : ''}`}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <span className="label" style={{ marginLeft: 16 }}>
          Severity ≥
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className={`chip${minSeverity === 0 ? ' is-on' : ''}`}
            onClick={() => setMinSeverity(0)}
          >
            all
          </button>
          {SEVERITIES.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${minSeverity === s ? ' is-on' : ''}`}
              onClick={() => setMinSeverity(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <span className="label" style={{ marginLeft: 16 }}>
          Módulo
        </span>
        <input
          className="input"
          placeholder="ej. english"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value.trim())}
          style={{ width: 160 }}
        />
        <label className="group" style={{ marginLeft: 16 }}>
          <input
            type="checkbox"
            checked={includeSynthetic}
            onChange={(e) => setIncludeSynthetic(e.target.checked)}
          />
          <span>incluir sintéticos</span>
        </label>
        {regressionOf && (
          <span className="group" style={{ marginLeft: 16 }}>
            <span className="label">Regressions de</span>
            <Link to={`/findings/${regressionOf}`} className="mono">
              {shortId(regressionOf, 8)}
            </Link>
            <button
              type="button"
              className="chip"
              data-testid="findings-clear-regression"
              title="Quitar el filtro de regressions"
              onClick={() => setRegressionOf(null)}
            >
              ×
            </button>
          </span>
        )}
      </section>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <section className="card" style={{ padding: 0 }}>
          {query.data.items.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              No hay findings con estos filtros.
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Sev</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th>Title</th>
                  <th style={{ width: 130 }}>Category</th>
                  <th style={{ width: 110 }}>Module</th>
                  <th style={{ width: 130 }}>Session</th>
                  <th style={{ width: 110 }}>Cuándo</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((it: FindingListItem) => (
                  <tr key={it.id}>
                    <td>
                      <SeverityBadge value={it.severity} />
                    </td>
                    <td>
                      <StatusBadge status={it.status} />
                    </td>
                    <td>
                      <Link to={`/findings/${it.id}`}>
                        {it.gated ? (
                          <span className="t-meta">{GATED_CONTENT_PLACEHOLDER}</span>
                        ) : (
                          it.title
                        )}
                      </Link>
                      {it.fromSynthetic && (
                        <span
                          className="badge"
                          title="Finding derivado de una sesión sintética del simulator"
                          style={{ marginLeft: 8 }}
                        >
                          synth
                        </span>
                      )}
                      {it.regressionOf && (
                        <Link
                          to={`/findings/${it.regressionOf}`}
                          title="Este finding fue matcheado contra un fix shipped previo — el bug volvió"
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginLeft: 8 }}
                        >
                          <span className="badge b-danger">🚨 regression</span>
                        </Link>
                      )}
                    </td>
                    <td className="mono">{it.category ?? '—'}</td>
                    <td className="mono">{it.moduleSlug}</td>
                    <td>
                      <Link to={`/sessions/${it.sessionId}`}>{shortId(it.sessionId, 8)}</Link>
                    </td>
                    <td title={it.createdAt}>{relativeTime(it.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}
